import { useEffect, useRef } from 'react';

import { useAtom, useSetAtom } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Modal, ModalBody, ModalHeader } from '@cfa/react-components';

import { useAuth } from '@/hooks/useAuth';
import { useNativeLocationPickerListener } from '@/hooks/useNativeListener';

import { hasNumbersOnlyInString } from '@/utils/string';

import LoadMorePaginator from '@/components/LoadMorePaginator/LoadMorePaginator';
import Searchbar from '@/components/Searchbar/Searchbar';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { LocationCard } from './LocationCard';

import { LocationDetails } from '@/_generated/api-clients/xp';
import {
  DataViewAtomFamilyKey,
  sourceDataFamily,
  useDataView,
} from '@/atoms/data-view';
import { licenseeLocationAtom } from '@/atoms/licensee-location';

const LicenseeLocationPicker = () => {
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data } = useAuth();
  const licenseeLocations = data?.licenseeLocations;
  const isLicensee = data?.isLicensee;
  const userIsLicenseeStaff = data?.userIsLicenseeStaff;
  const userSelectedLocation = data?.selectedLocation;

  const [
    { selected: selectedLicenseeLocation, shouldShowPicker: shouldShowPicker },
    sendLicenseeLocationAction,
  ] = useAtom(licenseeLocationAtom);

  const storedLicenseeLocation = localStorage.getItem('licenseeLocation');

  const [
    {
      display: { items, total },
      filters: { search },
    },
    { setSearch, resetSearch, showMore, resetAll },
  ] = useDataView<LocationDetails>(DataViewAtomFamilyKey.LICENSEE_LOCATIONS);

  // listening for Native message when location changed in native
  useNativeLocationPickerListener();

  // overriding old default location numbers set to 0, should be able to remove this in a month or two.
  useEffect(() => {
    if (Number(selectedLicenseeLocation?.number) === 0) {
      sendLicenseeLocationAction('setActive', {});
    }
  }, [selectedLicenseeLocation?.number, sendLicenseeLocationAction]);

  // sets initial Licensee Location
  useEffect(() => {
    if (
      // sets Licensee Location if selectedLocation exists in auth
      !!licenseeLocations?.length &&
      !!userSelectedLocation &&
      selectedLicenseeLocation?.number === undefined &&
      !!licenseeLocations.find(loc => loc.number === userSelectedLocation)
        ?.number
    ) {
      const matchedLocation = licenseeLocations.find(
        loc => loc.number === userSelectedLocation,
      );
      sendLicenseeLocationAction('setActive', matchedLocation ?? null);
    } else if (
      // sets Licensee Location or shows picker if location not selected (selectedLocation is null in auth)
      isLicensee &&
      !!licenseeLocations?.length &&
      !selectedLicenseeLocation
    ) {
      if (licenseeLocations.length < 2) {
        sendLicenseeLocationAction('setActive', licenseeLocations[0] ?? null);
      } else if (!window.native.isNative) {
        sendLicenseeLocationAction('show');
      }
    }
  }, [
    isLicensee,
    selectedLicenseeLocation,
    sendLicenseeLocationAction,
    licenseeLocations,
    userSelectedLocation,
  ]);

  const setSource = useSetAtom(sourceDataFamily('licensee-locations'));
  useEffect(() => {
    if (licenseeLocations && Array.isArray(licenseeLocations)) {
      setSource(licenseeLocations);
    }
  }, [setSource, licenseeLocations]);

  useEffect(() => {
    if (
      hasNumbersOnlyInString(selectedLicenseeLocation?.number) &&
      (licenseeLocations?.length ?? 0) > 1 &&
      storedLicenseeLocation
    ) {
      toast.custom(toastObj => (
        <ToastMessageBlock
          id={toastObj.id}
          title={t('TrainingPlans.toastMessage.nowViewingLocation1', {
            name: selectedLicenseeLocation?.name,
            number: selectedLicenseeLocation?.number,
          })}
        >
          {t('TrainingPlans.toastMessage.nowViewingLocation2')}
        </ToastMessageBlock>
      ));
      localStorage.removeItem('licenseeLocation');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLicenseeLocation]);

  const onClearSearchClick = () => {
    resetSearch();
    searchInputRef.current?.focus();
  };

  const showCloseBtn =
    !!userIsLicenseeStaff ||
    hasNumbersOnlyInString(selectedLicenseeLocation?.number);

  // @TODO Clean up these styling here to match figma.
  return (
    <Modal
      className="z-5000! [&_.cfa-modal-header]:pb-8"
      disableOverlayClick={!showCloseBtn}
      onClose={() => {
        sendLicenseeLocationAction('hide');
        resetAll();
      }}
      scrollMode="modal-body"
      show={shouldShowPicker}
      size="lg"
    >
      <ModalHeader showCloseButton={showCloseBtn} variant={'h2'}>
        {t('Generic.chooseLicenseeLocation')}
      </ModalHeader>
      <ModalBody>
        <Searchbar
          elevation={1}
          onChange={e => {
            setSearch(e.target.value, ['name', 'number']);
          }}
          onClear={onClearSearchClick}
          placeholder={t('Generic.searchLocations')}
          ref={searchInputRef}
          searchValue={search}
          showRecentSearchDropdown
        />
        {items.map((location, i) => (
          <LocationCard key={`${location.number}-${i}`} location={location} />
        ))}
        <LoadMorePaginator
          onClick={() => showMore()}
          showing={items.length}
          showingText={t('TrainingPlans.showingXOfYLocations', {
            showing: items.length,
            total: total,
          })}
          total={total}
        />
      </ModalBody>
    </Modal>
  );
};

export default LicenseeLocationPicker;
